import { useMemo, useState, type ChangeEvent } from 'react'
import { TableShell } from '../../components/molecules/TableShell'
import { Alert } from '../../components/atoms/Alert'
import { Badge } from '../../components/atoms/Badge'
import { Button } from '../../components/atoms/Button'
import { Input } from '../../components/atoms/Input'
import { Spinner } from '../../components/atoms/Spinner'
import { Th, Td } from '../../components/molecules/Table'
import { useAuth } from '../../../context/AuthContext'
import { SCREENCODES } from '../../../constants/screen.constants'
import {
  PREFERENCESSECTIONLABELS,
  findPreferenceEntry,
  type PreferenceSection
} from '../../../constants/preferences.constants'
import {
  filterBySection,
  usePreferences,
  type PreferenceConfigDto
} from '../../../viewmodels/usePreferences'

/**
 * /dashboard/preferences/settings — admin-only Preferences tab.
 *
 * Per Kiran 2026-05-14, renamed 2026-05-19 alongside System →
 * Preferences. Single page with 4 sub-tabs (Purge & Retention,
 * Email & OTP, Pagination, Session TTLs) — each tab renders a table of
 * rows from D1 `preferences_config` (migrations 0031 + 0034) with
 * inline-editable values. Save invalidates the worker-side CONFIG_KV
 * cache so changes take effect within ms.
 *
 * Auth: the `preferences.settings` screen permission is enforced at the
 * gateway. A user_type without that grant gets 403; the page shows the
 * error in the empty state. (Admin reaches it via its group grants — no
 * hardcoded super-admin bypass.)
 *
 * Legally-mandated rows (SUPABASEAUDITLOG = 2555 days for 7-year DPDP
 * compliance — INVOICES, ORDERDATA, BACKUPFILES retired with their
 * future-phase workers) get a "Legally mandated" badge and are
 * floor-bounded by the registry's `min` value — server rejects values
 * below the floor.
 */

const TABS: ReadonlyArray<PreferenceSection> = [
  'store',
  'purge',
  'email',
  'pagination',
  'sessions'
]

export function SettingsPage(): JSX.Element {
  const { items, isLoading, error, update, reload } = usePreferences()
  const [activeTab, setActiveTab] = useState<PreferenceSection>('purge')

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-heading text-h2 text-charcoal">Settings</h1>
      </header>

      <nav className="border-b border-border" aria-label="Settings sections">
        <ul className="flex flex-wrap gap-1 -mb-px">
          {TABS.map((tab) => {
            const isActive = tab === activeTab
            return (
              <li key={tab}>
                <button
                  type="button"
                  onClick={() => setActiveTab(tab)}
                  className={[
                    'px-4 py-2 font-body text-body-sm border-b-2 transition-colors',
                    isActive
                      ? 'border-forest text-forest font-medium'
                      : 'border-transparent text-text-secondary hover:text-charcoal'
                  ].join(' ')}
                  aria-current={isActive ? 'page' : undefined}
                >
                  {PREFERENCESSECTIONLABELS[tab]}
                </button>
              </li>
            )
          })}
        </ul>
      </nav>

      {error !== null ? (
        <Alert tone="error">
          {error}{' '}
          <button
            type="button"
            className="underline underline-offset-2 hover:text-charcoal"
            onClick={reload}
          >
            Retry
          </button>
        </Alert>
      ) : null}

      {isLoading && items.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Spinner />
        </div>
      ) : (
        <SectionTable items={filterBySection(items, activeTab)} onSave={update} />
      )}
    </div>
  )
}

interface SectionTableProps {
  readonly items: ReadonlyArray<PreferenceConfigDto>
  readonly onSave: (
    key: string,
    value: number | boolean | string
  ) => Promise<{ ok: true; data: PreferenceConfigDto } | { ok: false; field: string | null; message: string }>
}

function SectionTable({ items, onSave }: SectionTableProps): JSX.Element {
  if (items.length === 0) {
    return (
      <div className="text-center py-16 border border-border rounded-md bg-parchment/40">
        <p className="font-heading text-h3 text-charcoal">No tunables in this section yet</p>
        <p className="font-body text-body-sm text-text-secondary mt-2">
          The preferences worker reads <span className="font-mono">PREFERENCESREGISTRY</span> at runtime —
          if a new key was just added, redeploy the preferences worker and reload.
        </p>
      </div>
    )
  }

  return (
    <TableShell>
      <table className="w-full font-body text-body-sm">
        <thead className="bg-parchment/50 text-text-secondary">
          <tr>
            <Th>Key</Th>
            <Th>Description</Th>
            <Th className="text-right">Value</Th>
            <Th>Bounds</Th>
            <Th>Last updated</Th>
            <Th className="text-right">Save</Th>
          </tr>
        </thead>
        <tbody>
          {items.map((row) => (
            <Row key={row.key} row={row} onSave={onSave} />
          ))}
        </tbody>
      </table>
    </TableShell>
  )
}

interface RowProps {
  readonly row: PreferenceConfigDto
  readonly onSave: SectionTableProps['onSave']
}

function Row({ row, onSave }: RowProps): JSX.Element {
  const { can } = useAuth()
  const [draft, setDraft] = useState<string>(row.value)
  const [saving, setSaving] = useState<boolean>(false)
  const [savedAt, setSavedAt] = useState<number | null>(null)
  const [fieldError, setFieldError] = useState<string | null>(null)

  const dirty = draft.trim() !== row.value
  const bounds = useMemo(() => {
    if (row.min_value === null && row.max_value === null) return '—'
    if (row.min_value !== null && row.max_value !== null) return `${row.min_value} – ${row.max_value}`
    if (row.min_value !== null) return `≥ ${row.min_value}`
    return `≤ ${row.max_value!}`
  }, [row.min_value, row.max_value])

  function onChange(e: ChangeEvent<HTMLInputElement>): void {
    setDraft(e.target.value)
    setFieldError(null)
    setSavedAt(null)
  }

  /** Bool toggle setter — mirrors Product Source's Make/Buy pill. */
  function setBoolDraft(next: 'true' | 'false'): void {
    if (draft === next) return
    setDraft(next)
    setFieldError(null)
    setSavedAt(null)
  }

  async function onClickSave(): Promise<void> {
    if (!dirty || saving) return
    setSaving(true)
    setFieldError(null)
    const value: number | boolean | string =
      row.value_type === 'int'
        ? Number(draft.trim())
        : row.value_type === 'bool'
          ? draft.trim() === 'true' || draft.trim() === '1'
          : draft.trim()
    const res = await onSave(row.key, value)
    setSaving(false)
    if (res.ok) {
      setSavedAt(Date.now())
      setDraft(res.data.value)
    } else {
      setFieldError(res.message)
    }
  }

  return (
    <tr className="border-t border-border align-top">
      <Td>
        <div className="flex items-center gap-2">
          <span className="font-mono text-body-xs text-charcoal">{row.key}</span>
          {row.is_legally_mandated ? <Badge tone="warning">Legally mandated</Badge> : null}
        </div>
      </Td>
      <Td>
        <p className="font-body text-body-xs text-text-secondary max-w-[48ch]">
          {row.description}
        </p>
      </Td>
      <Td className="text-right">
        <div className="flex items-center justify-end gap-2">
          {row.value_type === 'bool' ? (
            // Pill-style segmented control — mirrors Product Source's Make/Buy.
            <div className="flex items-center gap-1 rounded-full border border-border p-1">
              {(['true', 'false'] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  disabled={saving || draft === v}
                  onClick={() => setBoolDraft(v)}
                  aria-pressed={draft === v}
                  className={`rounded-full px-4 py-1 font-body text-body-sm transition ${
                    draft === v
                      ? 'bg-forest text-charcoal shadow-sm'
                      : 'text-text-secondary hover:text-charcoal'
                  }`}
                >
                  {v === 'true' ? 'Yes' : 'No'}
                </button>
              ))}
            </div>
          ) : (
            <Input
              value={draft}
              onChange={onChange}
              inputMode={row.value_type === 'int' ? 'numeric' : 'text'}
              hasError={fieldError !== null}
              // min-w keeps the input tappable + readable on mobile
              // (the previous w-28 / 112px was too narrow under a finger).
              className="text-right font-mono min-w-[10rem]"
            />
          )}
          {(() => {
            const unit = findPreferenceEntry(row.key)?.unit ?? ''
            return unit.length > 0 ? (
              <span className="font-body text-body-xs text-text-secondary whitespace-nowrap min-w-[3.5rem] text-left">
                {unit}
              </span>
            ) : null
          })()}
        </div>
        {fieldError !== null ? (
          <span className="block text-body-xs text-terra mt-1">{fieldError}</span>
        ) : null}
      </Td>
      <Td>
        <span className="font-mono text-body-xs text-text-secondary whitespace-nowrap">
          {bounds}
        </span>
      </Td>
      <Td>
        <span className="font-body text-body-xs text-text-secondary whitespace-nowrap">
          {row.updated_at.length > 0 ? new Date(row.updated_at).toLocaleString() : '—'}
        </span>
      </Td>
      <Td className="text-right whitespace-nowrap">
        {can(SCREENCODES.PREFERENCESSETTINGS, 'edit') ? (
          <Button size="sm" variant="primary" disabled={!dirty || saving} onClick={onClickSave}>
            {saving ? 'Saving…' : savedAt !== null && !dirty ? 'Saved' : 'Save'}
          </Button>
        ) : null}
      </Td>
    </tr>
  )
}
